import { useState } from 'react';

import { UNTITLED_CONVERSATION } from '@gasbi/shared/common/components/AgentChat';
import { ChatHeader, ChatLayout } from '@gasbi/shared/common/components/Chat';
import { formatRelativeTime } from '@gasbi/shared/common/components/Chat/format';

import { AgentSelect } from '@/components/AgentSelect';
import { ChatComposer } from '@/components/ChatComposer';
import { ChatThread } from '@/components/ChatThread';
import { TIME_LABELS } from '@/components/timeLabels';
import {
  useGetChatAgentsQuery,
  useGetChatConversationsQuery,
} from '@/features/agents/agentsApi';
import { chatAppLabel } from '@/features/agents/chatApps';
import { useAgentChat } from '@/features/agents/useAgentChat';
import { useRetryOnUnauthorized } from '@/features/agents/useRetryOnUnauthorized';
import { ConversationSidebar } from '@/pages/GasbiAi/components/ConversationSidebar';
import { TopBar } from '@/pages/GasbiAi/components/TopBar';

import { Box, Stack } from '@mui/joy';

/** The service pages the list; a user's chats fit one page here, as on the CRM page. */
const PAGE_SIZE = 100;

const HINT =
  'Enter — надіслати · Shift+Enter — новий рядок · відповіді лише в межах ваших організацій';

/**
 * `/` — the full page «Gasbi AI» (ADR-0004 §2.1, §2.8): every conversation of the user from any app's panel or from
 * here, every published agent. A conversation started here belongs to no app.
 */
export const GasbiAiPage = () => {
  useRetryOnUnauthorized();
  const [app, setApp] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  // A list item carries no app: the one the list was filtered by when the conversation was opened, if any.
  const [startedIn, setStartedIn] = useState<string | null>(null);

  const agentsQuery = useGetChatAgentsQuery(undefined);
  const conversationsQuery = useGetChatConversationsQuery({
    app: app ?? undefined,
    page: 1,
    pageSize: PAGE_SIZE,
  });
  const agents = agentsQuery.data ?? [];
  const chat = useAgentChat({ agents, enabled: true });
  const { conversation } = chat;

  const openConversation = (id: string | null) => {
    chat.open(id);
    setStartedIn(id ? app : null);
  };

  const startedInLabel = chatAppLabel(startedIn);
  const subtitle = conversation
    ? [
        startedInLabel && `Почато в ${startedInLabel}`,
        formatRelativeTime(conversation.createdAt, TIME_LABELS),
      ]
        .filter(Boolean)
        .join(' · ')
    : undefined;

  return (
    <Stack sx={{ height: '100dvh' }}>
      <TopBar />
      <Box sx={{ flex: 1, minHeight: 0 }}>
        <ChatLayout
          sidebar={
            <ConversationSidebar
              items={conversationsQuery.data?.items ?? []}
              loading={conversationsQuery.isLoading}
              selectedId={chat.conversationId}
              onSelect={openConversation}
              onCreate={() => openConversation(null)}
              search={search}
              onSearchChange={setSearch}
              app={app}
              onAppChange={setApp}
            />
          }
          header={
            <ChatHeader
              title={conversation?.title || UNTITLED_CONVERSATION}
              subtitle={subtitle}
              actions={
                agents.length > 0 && (
                  <AgentSelect
                    agents={agents}
                    value={chat.agent?.id}
                    onChange={chat.selectAgent}
                  />
                )
              }
            />
          }
          composer={
            <ChatComposer
              chat={chat}
              placeholder='Запитайте агента про ваші дані…'
              hint={HINT}
            />
          }
        >
          <ChatThread chat={chat} />
        </ChatLayout>
      </Box>
    </Stack>
  );
};
